/* eslint-disable */
import React, { useState } from 'react';
import { Icon } from './Primitives';

const STEPS = [
  {
    id: 'type',
    question: 'What are you brewing?',
    sub: 'We\'ll narrow it down from here.',
    options: [
      { value: 'beans', label: 'Coffee Beans', desc: 'Grind fresh, brew your way', icon: '☕' },
      { value: 'pods', label: 'Espresso Pods', desc: 'One-touch capsule machines', icon: '⚡' },
    ],
  },
  {
    id: 'intensity',
    question: 'How strong do you like it?',
    sub: 'Think of your usual cup.',
    options: [
      { value: 'low', label: 'Light & Bright', desc: 'Delicate, tea-like, floral', icon: '🌸', range: [1, 5] },
      { value: 'medium', label: 'Balanced', desc: 'Smooth, round, everyday', icon: '☀️', range: [5, 8] },
      { value: 'high', label: 'Bold & Dark', desc: 'Intense, punchy, full-bodied', icon: '🌑', range: [8, 12] },
    ],
  },
  {
    id: 'notes',
    question: 'What flavors do you enjoy?',
    sub: 'Pick everything that sounds good.',
    multi: true,
    options: [
      { value: 'floral', label: 'Floral', desc: 'Jasmine, rose, lavender', icon: '🌺' },
      { value: 'fruit', label: 'Fruity', desc: 'Citrus, berry, stone fruit', icon: '🍑' },
      { value: 'chocolate', label: 'Chocolate', desc: 'Cocoa, dark chocolate, mocha', icon: '🍫' },
      { value: 'caramel', label: 'Caramel & Sweet', desc: 'Honey, toffee, brown sugar', icon: '🍮' },
      { value: 'nutty', label: 'Nutty', desc: 'Almond, hazelnut, walnut', icon: '🥜' },
      { value: 'earthy', label: 'Earthy & Spicy', desc: 'Cedar, smoke, dark fruit', icon: '🌿' },
    ],
  },
  {
    id: 'roast',
    question: 'Roast preference?',
    sub: 'Only applies to beans — pods have a fixed roast.',
    beanOnly: true,
    options: [
      { value: 'Light', label: 'Light Roast', desc: 'Bright acidity, complex origin character', icon: '🟡' },
      { value: 'Medium', label: 'Medium Roast', desc: 'Balanced, approachable, versatile', icon: '🟠' },
      { value: 'Dark', label: 'Dark Roast', desc: 'Bold, low acidity, smoky finish', icon: '🔴' },
      { value: 'any', label: 'No preference', desc: 'Surprise me', icon: '🎲' },
    ],
  },
];

const NOTE_MAP = {
  floral: ['jasmine', 'bergamot', 'hibiscus', 'floral', 'tea'],
  fruit: ['peach', 'citrus', 'apple', 'plum', 'lychee', 'blackcurrant', 'rhubarb', 'mandarin', 'apricot'],
  chocolate: ['cocoa', 'chocolate', 'dark cocoa', 'mocha'],
  caramel: ['caramel', 'honey', 'toffee', 'brown sugar', 'sugar'],
  nutty: ['hazelnut', 'almond', 'walnut', 'pecan', 'praline'],
  earthy: ['cedar', 'smoke', 'fig', 'molasses', 'earthy', 'oak'],
};

function scoreProduct(product, answers) {
  let score = 0;
  const intensityMap = { low: [1, 5], medium: [5, 8], high: [8, 12] };
  const [min, max] = intensityMap[answers.intensity] || [1, 12];
  if (product.intensity) {
    if (product.intensity >= min && product.intensity <= max) score += 3;
    else score -= 1;
  }
  if (answers.notes && answers.notes.length > 0 && product.notes) {
    const noteText = product.notes.toLowerCase();
    answers.notes.forEach(noteKey => {
      const keywords = NOTE_MAP[noteKey] || [];
      if (keywords.some(kw => noteText.includes(kw))) score += 2;
    });
  }
  if (product.type === 'beans' && answers.roast && answers.roast !== 'any') {
    if (product.roast === answers.roast) score += 2;
  }
  score += (product.rating - 4) * 2;
  return score;
}

export default function Quiz({ data, onClose, onOpen, addToCart }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ type: null, intensity: null, notes: [], roast: null });
  const [results, setResults] = useState(null);
  const [added, setAdded] = useState({});

  const visibleSteps = STEPS.filter(s => !(s.beanOnly && answers.type === 'pods'));
  const currentVisibleStep = visibleSteps[step];
  const totalSteps = visibleSteps.length;
  const progress = ((step) / totalSteps) * 100;

  const handleOption = (value) => {
    if (currentVisibleStep.multi) {
      const current = answers.notes || [];
      const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      setAnswers(a => ({ ...a, notes: next }));
    } else {
      const key = currentVisibleStep.id;
      setAnswers(a => ({ ...a, [key]: value }));
      setTimeout(() => advance({ ...answers, [key]: value }), 220);
    }
  };

  const advance = (currentAnswers = answers) => {
    const nextStep = step + 1;
    const nextVisible = visibleSteps[nextStep];
    if (!nextVisible) {
      computeResults(currentAnswers);
    } else {
      setStep(nextStep);
    }
  };

  const computeResults = (finalAnswers) => {
    const pool = data.products.filter(p => p.type === finalAnswers.type && p.stock);
    const scored = pool
      .map(p => ({ product: p, score: scoreProduct(p, finalAnswers) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map(x => x.product);
    setResults(scored);
  };

  const handleAdd = (p) => {
    addToCart(p, 1);
    setAdded(a => ({ ...a, [p.id]: true }));
  };

  const reset = () => {
    setStep(0);
    setAnswers({ type: null, intensity: null, notes: [], roast: null });
    setResults(null);
    setAdded({});
  };

  return (
    <div className="quiz-overlay" onClick={onClose}>
      <div className="quiz-panel" onClick={e => e.stopPropagation()}>

        <div className="quiz-header">
          <div>
            <span className="eyebrow">Product Finder</span>
            {!results && (
              <div className="quiz-progress-bar">
                <div className="quiz-progress-fill" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
          <button className="quiz-close" onClick={onClose}>
            <Icon name="close" className="icon icon--sm" />
          </button>
        </div>

        {results && (
          <div className="quiz-results">
            <div className="quiz-results-head">
              <h2>Your matches.</h2>
              <p>Based on your preferences — {results.length} products picked for you.</p>
            </div>
            <div className="quiz-results-grid">
              {results.map((p, i) => (
                <div key={p.id} className="quiz-result-card">
                  {i === 0 && <span className="quiz-result-badge">Best match</span>}
                  <div className="quiz-result-img">
                    {p.image
                      ? <img src={p.image} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      : <><div className="quiz-result-img-stripe" /><div className="quiz-result-img-label">{p.name}</div></>
                    }
                  </div>
                  <div className="quiz-result-info">
                    <span className="quiz-result-origin">{p.origin}</span>
                    <span className="quiz-result-name">{p.name}</span>
                    <span className="quiz-result-notes">{p.notes}</span>
                    {p.intensity && (
                      <div className="quiz-result-intensity">
                        {Array.from({ length: 12 }).map((_, idx) => (
                          <span key={idx} className={`quiz-int-bar ${idx < p.intensity ? 'on' : ''}`} />
                        ))}
                        <span className="quiz-int-val">{p.intensity}/12</span>
                      </div>
                    )}
                    <div className="quiz-result-footer">
                      <span className="quiz-result-price">${p.price}</span>
                      <div className="quiz-result-actions">
                        <button className="bw-btn bw-btn--ghost bw-btn--small" onClick={() => { onOpen(p); onClose(); }}>
                          View
                        </button>
                        <button
                          className={`bw-btn bw-btn--small ${added[p.id] ? 'bw-btn--ghost' : 'bw-btn--primary'}`}
                          onClick={() => handleAdd(p)}
                        >
                          {added[p.id] ? '✓ Added' : 'Add to Cart'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="quiz-results-footer">
              <button className="bw-btn bw-btn--ghost" onClick={reset}>Retake quiz</button>
              <button className="bw-btn bw-btn--primary" onClick={onClose}>Continue shopping</button>
            </div>
          </div>
        )}

        {!results && currentVisibleStep && (
          <div className="quiz-step">
            <div className="quiz-step-head">
              <span className="quiz-step-count">{step + 1} of {totalSteps}</span>
              <h2>{currentVisibleStep.question}</h2>
              <p>{currentVisibleStep.sub}</p>
            </div>
            <div className={`quiz-options ${currentVisibleStep.multi ? 'quiz-options--multi' : ''}`}>
              {currentVisibleStep.options.map(opt => {
                const isSelected = currentVisibleStep.multi
                  ? (answers.notes || []).includes(opt.value)
                  : answers[currentVisibleStep.id] === opt.value;
                return (
                  <button
                    key={opt.value}
                    className={`quiz-option ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => handleOption(opt.value)}
                  >
                    <span className="quiz-option-icon">{opt.icon}</span>
                    <span className="quiz-option-label">{opt.label}</span>
                    <span className="quiz-option-desc">{opt.desc}</span>
                    {isSelected && <span className="quiz-option-check">✓</span>}
                  </button>
                );
              })}
            </div>
            {currentVisibleStep.multi && (
              <div className="quiz-multi-footer">
                <button
                  className="bw-btn bw-btn--primary"
                  onClick={() => advance()}
                  disabled={answers.notes.length === 0}
                >
                  {answers.notes.length === 0 ? 'Select at least one' : `Continue with ${answers.notes.length} selected`}
                  <Icon name="arrowSm" className="icon icon--sm" />
                </button>
              </div>
            )}
            {step > 0 && (
              <button className="quiz-back" onClick={() => setStep(s => s - 1)}>
                ← Back
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
}